import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/HomePage.css';  // Import styles
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import HomeHeroCard from './partial/home/HomeHeroCard.jsx';
import HomePerformerCard from './partial/home/HomePerformerCard.jsx';
import HomePredictedCard from './partial/home/HomePredictedCard.jsx';
import HomeNotificationsCard from './partial/home/HomeNotificationsCard.jsx';
import { MAX_NOTIFICATIONS, useListenerDashboard } from '../hooks/useListenerDashboard.js';
import { useStreamStatus } from '../hooks/useStreamStatus.js';
import { useNotificationsContext } from '../contexts/NotificationsContext.jsx';
import { useLocale, useT } from '../contexts/LocaleContext.jsx';
import { mergeActivity } from '../_helpers/notifications.js';

/**
 * Prefer the real-time status once it has resolved, otherwise the initial
 * snapshot from `/home/feed` (ENG-730). Both carry the server-built player URL
 * (`embedUrl`, built as on `/public/bands/{id}/stream/status`). The URL follows the same gate as the
 * other fields: once the live status has resolved, its `null` means "nothing
 * to frame" (e.g. the source was removed mid-show), not "fall back to the
 * snapshot".
 */
const mergeStatus = (live, snapshot) => ({
  isLive: live.statusResolved ? live.isLive : Boolean(snapshot?.isLive),
  viewerCount: live.statusResolved ? live.viewerCount : (snapshot?.viewerCount ?? 0),
  embedUrl: live.statusResolved ? (live.embedUrl ?? null) : (snapshot?.embedUrl ?? null),
});

/**
 * Stands in for the hero card when there is no match to show (ENG-328): a
 * card-sized empty/error state in the hero slot so the rest of the dashboard
 * still renders. Labelled like the hero (eyebrow + <h1>) so the page keeps
 * its top-level heading.
 */
const HeroSlotState = ({ title, children, testId }) => {
  const t = useT();
  return (
    <section
      className="listener-home-state listener-home-state--card"
      aria-labelledby="listener-home-state-title"
      data-testid={testId}
    >
      <span className="listener-home-eyebrow">{t('home.hero.eyebrow')}</span>
      <h1 id="listener-home-state-title" className="listener-home-state-title">{title}</h1>
      {children}
    </section>
  );
};

/** Second-row modifier by how many performer cards sit beside the side column. */
const rowClassName = (performerCards) => {
  // 0: the side column is the whole row; 1: it spans the two remaining columns.
  const modifier = performerCards === 0 ? ' listener-home-row--side-only' : performerCards === 1 ? ' listener-home-row--side-wide' : '';
  return `listener-home-row${modifier}`;
};

function HomePage() {
    const navigate = useNavigate();
    const { t, locale } = useLocale();
    const {
      featured,
      statusById,
      queuesById,
      predictedEvents,
      predictedEventsLoading,
      predictedEventsError,
      notifications,
      loading,
      error,
      canShuffle,
      shuffleRandomPick,
      reload,
      reloadPredictedEvents,
    } = useListenerDashboard();

    // Live-stream notifications (ENG-26) join the request/tip activity in the
    // card, newest first; the backend unread count adds to the "new" chip.
    const liveFeed = useNotificationsContext();
    const cardItems = useMemo(
      () => mergeActivity(liveFeed.items, notifications.items, MAX_NOTIFICATIONS, t, locale),
      [liveFeed.items, notifications.items, t, locale]
    );
    const cardNewCount = notifications.newCount + liveFeed.unread.count;

    useEffect(() => {
      document.body.classList.add('home-page-body');
      return () => document.body.classList.remove('home-page-body');
    }, []);

    const { match, biggestCrowd, randomPick } = featured;
    const matchLive = useStreamStatus(match?.ID ?? null);
    const crowdLive = useStreamStatus(biggestCrowd?.ID ?? null);
    const randomLive = useStreamStatus(randomPick?.ID ?? null);

    const matchStatus = mergeStatus(matchLive, statusById[match?.ID]);
    const crowdStatus = mergeStatus(crowdLive, statusById[biggestCrowd?.ID]);
    const randomStatus = mergeStatus(randomLive, statusById[randomPick?.ID]);

    const openStream = useCallback((band) => {
      if (band) navigate(`/performers/${band.ID}/stream`);
    }, [navigate]);
    const openBand = useCallback((band) => {
      if (band) navigate(`/performers/${band.ID}`);
    }, [navigate]);
    // Mark read first (rolls back on failure), then go where the backend said.
    const openNotification = useCallback((item) => {
      if (!item?.destination) return;
      liveFeed.markRead(item.notificationId);
      if (item.destination.kind === 'route') navigate(item.destination.to);
      else window.open(item.destination.href, '_blank', 'noopener,noreferrer');
    }, [liveFeed, navigate]);

    const renderBody = () => {
      if (loading) {
        return (
          <div className="listener-home-state" data-testid="home-loading">
            <LoadingSpinner />
          </div>
        );
      }

      // A missing or failed recommendations list only affects the hero slot and
      // the performer cards (ENG-328). "Predicted for tonight" and the
      // listener's notifications load independently and still show.
      const renderHeroSlot = () => {
        if (error) {
          return (
            <HeroSlotState title={t('home.state.errorTitle')} testId="home-error">
              <p className="listener-home-state-text" role="alert">{t(error.key, error.params)}</p>
              <button type="button" className="listener-home-hero-watch" onClick={reload}>
                {t('common.actions.tryAgain')}
              </button>
            </HeroSlotState>
          );
        }
        if (!match) {
          return (
            <HeroSlotState title={t('home.state.emptyTitle')} testId="home-empty">
              <p className="listener-home-state-text">{t('home.state.emptyText')}</p>
              <button type="button" className="listener-home-hero-watch" onClick={() => navigate('/performers')}>
                {t('home.state.findPerformers')}
              </button>
            </HeroSlotState>
          );
        }
        const matchQueue = queuesById[match.ID] ?? { current: null, upcoming: [] };
        return (
          <HomeHeroCard
            band={match}
            isLive={matchStatus.isLive}
            embedUrl={matchStatus.embedUrl}
            currentSong={matchQueue.current}
            upcoming={matchQueue.upcoming}
            matchScore={featured.matchScore}
            onWatch={() => openStream(match)}
          />
        );
      };

      const performerCards = [biggestCrowd, randomPick].filter(Boolean).length;

      return (
        <>
          {renderHeroSlot()}

          <div className={rowClassName(performerCards)}>
            {biggestCrowd && (
              <HomePerformerCard
                // Only claim "Biggest crowd" while the band actually has one.
                eyebrow={crowdStatus.isLive ? t('home.performer.biggestCrowd') : t('home.performer.recommendedForYou')}
                band={biggestCrowd}
                isLive={crowdStatus.isLive}
                viewerCount={crowdStatus.viewerCount}
                embedUrl={crowdStatus.embedUrl}
                currentSong={queuesById[biggestCrowd.ID]?.current ?? null}
                onOpen={() => openStream(biggestCrowd)}
              />
            )}
            {randomPick && (
              <HomePerformerCard
                eyebrow={t('home.performer.randomPick')}
                band={randomPick}
                isLive={randomStatus.isLive}
                viewerCount={randomStatus.viewerCount}
                embedUrl={randomStatus.embedUrl}
                currentSong={queuesById[randomPick.ID]?.current ?? null}
                onOpen={() => openStream(randomPick)}
                onShuffle={canShuffle ? shuffleRandomPick : undefined}
              />
            )}
            <div className="listener-home-side-column">
              <HomePredictedCard
                events={predictedEvents}
                loading={predictedEventsLoading}
                error={predictedEventsError}
                onRetry={reloadPredictedEvents}
                onSeeAll={() => navigate('/performers')}
                onOpenBand={openBand}
              />
              <HomeNotificationsCard
                items={cardItems}
                newCount={cardNewCount}
                onSeeAll={() => navigate('/notifications')}
                onOpen={openNotification}
              />
            </div>
          </div>
        </>
      );
    };

    return (
      <div className="listener-home">
        <StreamPageHeader />
        <main className="listener-home-content">
          {renderBody()}
        </main>
      </div>
    );
}

export default HomePage;
